if (!customElements.get('sticky-add-to-cart')) {
  class StickyAddToCart extends HTMLElement {
    constructor() {
      super();

      this._isStuck = false;
      this._hiddenByBottom = false;
      this._currentQuantity = 1;
      this._targetAddToCartButton = null;
      this._buyButtonsObserver = null;
      this._footerObserver = null;

      this._handleClick = this.handleClick.bind(this);
      this._handleVariantChange = this.handleVariantChange.bind(this);
      this._handleQuantityChange = this.handleQuantityChange.bind(this);
      this._handleCartAddComplete = this.handleCartAddComplete.bind(this);
    }

    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.cacheButtonRefs();

      this._currentQuantity = parseInt(this.dataset.initialQuantity || '1', 10) || 1;
      this.updateButtonText();

      this.addEventListener('click', this._handleClick);
      this.setupIntersectionObserver();
      this.setupQuantityListener();

      this._variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this._handleVariantChange);
      this._cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this._handleCartAddComplete);
      this._cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, this._handleCartAddComplete);
    }

    disconnectedCallback() {
      this._buyButtonsObserver?.disconnect();
      this._footerObserver?.disconnect();
      this._variantChangeUnsubscriber?.();
      this._cartUpdateUnsubscriber?.();
      this._cartErrorUnsubscriber?.();
      this.removeEventListener('click', this._handleClick);

      const quantityInput = this.getQuantityInput();
      if (quantityInput && this._quantityInputHandler) {
        quantityInput.removeEventListener('change', this._quantityInputHandler);
      }
    }

    handleClick(event) {
      if (event.target.closest('[data-sticky-info]')) {
        event.preventDefault();
        this.scrollToProduct();
        return;
      }

      if (!event.target.closest('[data-sticky-add-button]')) return;
      this.handleAddToCartClick();
    }

    scrollToProduct() {
      const productSection = this.closest('product-info');
      if (!productSection) return;

      productSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    handleAddToCartClick() {
      const targetButton = this.getTargetAddToCartButton();
      if (!targetButton || targetButton.disabled) return;

      targetButton.click();
      this.setLoading(true);
    }

    handleVariantChange({ data: { sectionId, html, variant } }) {
      if (sectionId !== this.dataset.section) return;

      const newSticky = html.querySelector('sticky-add-to-cart');
      if (!newSticky) return;

      const newBar = newSticky.querySelector('[data-sticky-bar]');
      const currentBar = this.stickyBar;
      if (!newBar || !currentBar) return;

      const currentStuck = currentBar.getAttribute('data-stuck') || 'false';

      currentBar.innerHTML = newBar.innerHTML;
      currentBar.setAttribute('data-stuck', currentStuck);

      this.dataset.variantAvailable = newSticky.dataset.variantAvailable;
      this.dataset.currentVariantId = newSticky.dataset.currentVariantId;

      this.cacheButtonRefs();
      this._targetAddToCartButton = null;

      if (!variant) {
        this.handleVariantUnavailable();
      }

      this.syncQuantityFromInput();
      this.updateButtonText();
    }

    handleVariantUnavailable() {
      this.dataset.currentVariantId = '';

      const variantTitleElement = this.querySelector('[data-sticky-variant-title]');
      const productInfo = this.closest('product-info');
      const variantSelects = productInfo?.querySelector('variant-selects');
      if (!variantTitleElement || !variantSelects) return;

      const selectedOptions = Array.from(variantSelects.querySelectorAll('.form__label'))
        .map((legend) => {
          const value = legend.querySelector('[data-selected-value]')?.textContent.trim();
          if (!value) return '';

          const name = Array.from(legend.childNodes)
            .find((node) => node.nodeType === Node.TEXT_NODE)
            ?.textContent.replace(/:$/, '')
            .trim();

          return name ? `${name}: ${value}` : value;
        })
        .filter(Boolean)
        .join(' / ');

      if (!selectedOptions) return;
      variantTitleElement.textContent = selectedOptions;
    }

    handleCartAddComplete() {
      this.setLoading(false);
    }

    setLoading(isLoading) {
      this.addToCartButton?.classList.toggle('loading', isLoading);
      this.loadingText?.classList.toggle('hidden', !isLoading);
    }

    cacheButtonRefs() {
      this.stickyBar = this.querySelector('[data-sticky-bar]');
      this.addToCartButton = this.querySelector('[data-sticky-add-button]');
      this.quantityDisplay = this.querySelector('[data-sticky-quantity-display]');
      this.quantityNumber = this.querySelector('[data-sticky-quantity-number]');
      this.loadingText = this.querySelector('.loading-overlay__text');
    }

    handleQuantityChange() {
      this.syncQuantityFromInput();
      this.updateButtonText();
    }

    setupQuantityListener() {
      const quantityInput = this.getQuantityInput();
      if (!quantityInput) return;

      this._quantityInputHandler = this._handleQuantityChange;
      quantityInput.addEventListener('change', this._quantityInputHandler);
      this.syncQuantityFromInput();
    }

    syncQuantityFromInput() {
      const quantityInput = this.getQuantityInput();
      if (!quantityInput) return;
      this._currentQuantity = parseInt(quantityInput.value, 10) || 1;
    }

    getQuantityInput() {
      return this.closest('product-info')?.querySelector('.quantity__input');
    }

    setupIntersectionObserver() {
      const buyButtonsBlock = this.closest('product-info')?.querySelector('.product-form__wrapper');
      if (!buyButtonsBlock || !this.stickyBar) return;

      const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
      if (!footer) return;

      this._targetAddToCartButton = this.getTargetAddToCartButton();

      this._buyButtonsObserver = new IntersectionObserver((entries) => {
        const [entry] = entries;
        if (!entry) return;

        if (!entry.isIntersecting && !this._isStuck) {
          const rect = entry.target.getBoundingClientRect();
          if (rect.bottom < 0 || rect.top < 0) {
            this.showStickyBar();
          }
        } else if (entry.isIntersecting && this._isStuck) {
          this._hiddenByBottom = false;
          this.hideStickyBar();
        }
      });

      this._footerObserver = new IntersectionObserver(
        (entries) => {
          const [entry] = entries;
          if (!entry) return;

          if (entry.isIntersecting && this._isStuck) {
            this._hiddenByBottom = true;
            this.hideStickyBar();
          } else if (!entry.isIntersecting && this._hiddenByBottom) {
            const rect = buyButtonsBlock.getBoundingClientRect();
            if (rect.bottom < 0 || rect.top < 0) {
              this._hiddenByBottom = false;
              this.showStickyBar();
            }
          }
        },
        { rootMargin: '200px 0px 0px 0px' }
      );

      this._buyButtonsObserver.observe(buyButtonsBlock);
      this._footerObserver.observe(footer);
    }

    showStickyBar() {
      if (!this.stickyBar) return;
      this._isStuck = true;
      this.stickyBar.dataset.stuck = 'true';
    }

    hideStickyBar() {
      if (!this.stickyBar) return;
      this._isStuck = false;
      this.stickyBar.dataset.stuck = 'false';
    }

    getTargetAddToCartButton() {
      if (this._targetAddToCartButton?.isConnected) return this._targetAddToCartButton;

      const productForm = this.getProductForm();
      this._targetAddToCartButton = productForm?.querySelector('[type="submit"][name="add"]') ?? null;
      return this._targetAddToCartButton;
    }

    getProductForm() {
      const sectionId = this.dataset.section;
      if (!sectionId) return null;

      return document.querySelector(`#product-form-${sectionId}`)?.closest('product-form') ?? null;
    }

    updateButtonText() {
      if (!this.addToCartButton || !this.quantityDisplay || !this.quantityNumber) return;

      const available = !this.addToCartButton.disabled;

      this.quantityNumber.textContent = this._currentQuantity.toString();
      this.quantityDisplay.style.display = available && this._currentQuantity > 1 ? 'inline' : 'none';
    }
  }

  customElements.define('sticky-add-to-cart', StickyAddToCart);
}
